import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DIALOG_SIZE, DialogService, DrButtonComponent, DrCardComponent } from '@datarailsshared/datarailsshared';
import { UpgradeDialogComponent } from '@shared/components/upgrade-dialog/upgrade-dialog.component';
import { UserSessionStateService } from '../../services/user-session-state.service';

/**
 * In-app landing shown when the BE returns `401 + extensions.code ===
 * "SESSION_EXPIRED"` (sliding 30-minute idle TTL elapsed). Renders in place
 * of the routed wizard while `UserSessionStateService.sessionExpired$` is
 * latched on (see `SessionRetryInterceptor`).
 *
 * The CTA is the user's first chance to acknowledge their progress is gone:
 * we keep the latch on until they click so a refresh — which would otherwise
 * also re-hit a 401 — still surfaces this screen rather than the wizard.
 *
 * Restart path is deliberately lazy: we wipe local state and navigate to
 * `/onboarding/setup`, leaving session minting to the wizard's normal flow
 * (`UserSessionService.createSession()` on the Welcome → ERP-Next click).
 * Eagerly minting here would defeat `onboardingWelcomeGuard`'s "no session
 * → show Welcome" branch and skip the first step.
 */
@Component({
    selector: 'app-session-expired-landing',
    standalone: true,
    imports: [DrCardComponent, DrButtonComponent],
    templateUrl: './session-expired-landing.component.html',
    styleUrls: ['./session-expired-landing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionExpiredLandingComponent {
    private readonly state = inject(UserSessionStateService);
    private readonly router = inject(Router);
    private readonly dialogService = inject(DialogService);

    handleRestart(): void {
        this.state.restartAfterSessionExpiry();
        this.router.navigate(['/onboarding/setup']);
    }

    /**
     * Opens the shared `UpgradeDialogComponent` — same surface as the
     * "Unlock full version" CTA in `ai-agents.component.ts` and the upgrade
     * banner in the AI-agent conversation input. Kept identical (dialog
     * config, sizing, hidden close button) so the upgrade path stays a
     * single visual contract across the app.
     */
    openUpgradeDialog(): void {
        this.dialogService.openDialogWrapper(UpgradeDialogComponent, {
            panelClass: ['upgrade-dialog-panel'],
            data: {
                hideCloseBtn: true,
                theme: { themeSize: DIALOG_SIZE.SMALL, contentNoPadding: true },
            },
        });
    }
}
